import React from 'react'
import { useDispatch } from 'react-redux'
import Image from 'next/image'
import ReactAudioPlayer from 'react-audio-player'
import { audioExtensions, gifExtensions, image3DExtensions, imageExtensions, videoExtensions } from 'utils/constants'
import { ImageViewMenu, Viewer3D, JsonViewer } from 'components/index.js'
import BeforeAfterSlider from './BeforeAfterSlider'
import { downloadFile } from 'utils/fileUtils'
import { copyToClipboard, defaultImage } from 'utils/mediaUtils'

const MediaRenderer = ({
  item,
  examples,
  isWatermark,
  isBeforeAfterModel,
  beforeImage,
  loading,
  classNames = '',
  imageClassNames = '',
  image3dClassNames = '',
  videoClassNames = '',
  textClassNames = '',
  audioClassNames = '',
  onClick = () => {},
  enableCopy = true,
  copyOnHover = true,
  videoControl = true,
  videoAutoPlay = false,
  mediaKey,
}) => {
  const dispatch = useDispatch()

  // Safety check to prevent errors
  if (!item || typeof item !== 'string') {
    return (
      <div className={`flex items-center justify-center w-full h-32 bg-gray-100 rounded-lg ${classNames}`}>
        <span className="text-gray-500 text-sm">Invalid media file</span>
      </div>
    )
  }

  const ext = item.split('.').pop()

  if (ext === 'mtl') return null

  const handleWatermark = (image) => {
    if (!isWatermark) return image
    return image
  }

  // Determine if hover effects should be enabled
  const enableHoverEffects = copyOnHover && enableCopy
  const is3DModel = image3DExtensions.includes(ext)

  return (
    <div
      className={`relative overflow-hidden rounded-lg transition-all duration-300 ${
        enableHoverEffects
          ? 'group shadow-md hover:shadow-xl hover:scale-[1.02] bg-gradient-to-br from-gray-900/5 to-gray-900/10'
          : is3DModel
            ? 'bg-white'
            : 'bg-transparent'
      } ${classNames}`}
      onClick={onClick}
    >
      {/* Overlay for hover effects - only show if copyOnHover is enabled */}
      {enableHoverEffects && (
        <div className="absolute inset-0 z-10 transition-all duration-300 pointer-events-none bg-black/0 group-hover:bg-black/20" />
      )}

      {examples ? (
        <div className="relative w-full aspect-square">
          {videoExtensions.includes(ext) ? (
            <video
              src={item}
              className={`object-cover w-full h-full rounded-lg ${videoClassNames}`}
              width="100%"
              height="100%"
            />
          ) : audioExtensions.includes(ext) ? (
            <ReactAudioPlayer
              src={item}
              controls
              className="p-2 w-full h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg shadow-md"
            />
          ) : image3DExtensions.includes(ext) ? (
            <Viewer3D
              file={item}
              classNames={`w-full h-full bg-white rounded-lg ${image3dClassNames}`}
              key={`${mediaKey}-3d`}
              enableRotation={true}
              enablePan={false}
              enableZoom={true}
              autoRotate={false}
            />
          ) : (
            <Image
              src={imageExtensions.includes(ext) ? item : defaultImage}
              alt="example"
              fill
              className={`!relative w-full h-full object-cover rounded-lg ${
                enableHoverEffects ? 'transition-transform duration-300 group-hover:scale-105' : ''
              }`}
            />
          )}
        </div>
      ) : (
        <div className="relative w-full aspect-square">
          {videoExtensions.includes(ext) ? (
            <video
              src={item}
              controls={videoControl}
              autoPlay={videoAutoPlay}
              className={`object-cover w-full h-full rounded-lg ${videoClassNames}`}
              width="100%"
              height="100%"
            >
              <track kind="captions" />
            </video>
          ) : gifExtensions.includes(ext) ? (
            isBeforeAfterModel ? (
              <BeforeAfterSlider beforeImage={beforeImage} afterImage={item} />
            ) : (
              <Image
                fill
                className={`!relative w-full h-full object-cover rounded-lg ${
                  enableHoverEffects ? 'transition-transform duration-300 group-hover:scale-105' : ''
                } ${imageClassNames}`}
                src={item}
                alt="media"
              />
            )
          ) : audioExtensions.includes(ext) ? (
            <ReactAudioPlayer
              src={item}
              className={`p-2 w-full h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg shadow-md ${audioClassNames}`}
              controls
              width="100%"
              height="100%"
            />
          ) : ['json'].includes(ext) ? (
            <JsonViewer
              data={item}
              isLink={true}
              classNames={`w-full h-full p-4 bg-gray-800/90 rounded-lg text-white backdrop-blur-sm ${textClassNames}`}
            />
          ) : isBeforeAfterModel ? (
            <BeforeAfterSlider beforeImage={beforeImage} afterImage={handleWatermark(item)} />
          ) : image3DExtensions.includes(ext) ? (
            <Viewer3D
              file={item}
              classNames={`w-full h-full bg-white rounded-lg ${image3dClassNames}`}
              key={`${mediaKey}-3d`}
              enableRotation={true}
              enablePan={false}
              enableZoom={true}
              autoRotate={false}
            />
          ) : (
            <Image
              fill
              src={handleWatermark(item)}
              alt="media"
              className={`!relative w-full h-full object-cover rounded-lg ${
                enableHoverEffects ? 'transition-transform duration-300 group-hover:scale-105' : ''
              } ${imageClassNames}`}
            />
          )}

          {/* Only show ImageViewMenu if enableCopy and copyOnHover are both true */}
          {enableCopy && copyOnHover && (
            <ImageViewMenu
              className={`
                opacity-100
                absolute top-2 right-2 z-20
                flex gap-2 p-2 bg-gray-900/80 backdrop-blur-md rounded-lg
                border shadow-lg border-gray-700/50`}
              loading={loading}
              handleImageDownload={() => {
                const promptContext = {
                  prompt: mediaKey || '',
                  mode: 'content',
                  title: '',
                }
                downloadFile(item, promptContext)
              }}
              handleUrlCopy={() => dispatch(copyToClipboard(item))}
            />
          )}
        </div>
      )}

      {loading && (
        <div className="flex absolute inset-0 z-30 justify-center items-center rounded-lg bg-black/50">
          <div className="w-8 h-8 rounded-full border-4 border-blue-500 animate-spin border-t-transparent" />
        </div>
      )}
    </div>
  )
}

export default MediaRenderer
